/* eslint-disable no-restricted-syntax */
import { SeverityNumber, logs } from "@opentelemetry/api-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { LoggerProvider, SimpleLogRecordProcessor } from "@opentelemetry/sdk-logs";
import { env } from "@/env";

// Type definitions for log levels
export type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";

interface LogContext {
  [key: string]: unknown;
}

class PostHogLogger {
  private logger: ReturnType<typeof logs.getLogger> | null = null;
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private initialize() {
    // Only initialize if PostHog is configured
    if (!env.VITE_POSTHOG_KEY || !env.VITE_POSTHOG_HOST) {
      // Silent fail - PostHog logging is optional
      return;
    }

    try {
      // Use EU endpoint for logs
      const logsEndpoint = `${env.VITE_POSTHOG_HOST}/i/v1/logs`;

      // Create OTLP exporter with PostHog configuration
      const exporter = new OTLPLogExporter({
        url: logsEndpoint,
        headers: {
          Authorization: `Bearer ${env.VITE_POSTHOG_KEY}`,
        },
      });

      // Create logger provider with processor
      const processor = new SimpleLogRecordProcessor(exporter);
      const loggerProvider = new LoggerProvider({
        processors: [processor],
      });

      // Register the logger provider
      logs.setGlobalLoggerProvider(loggerProvider);

      // Get logger instance
      this.logger = logs.getLogger("boomer-app", "1.0.0");
      this.isInitialized = true;

      // Use console.log only for initialization success
      if (env.DEV) {
        console.log("PostHog logging initialized successfully");
      }
    } catch (error) {
      // Use console.error only for critical initialization errors
      if (env.DEV) {
        console.error("Failed to initialize PostHog logging:", error);
      }
    }
  }

  private getSeverityNumber(level: LogLevel): SeverityNumber {
    switch (level) {
      case "debug":
        return SeverityNumber.DEBUG;
      case "info":
        return SeverityNumber.INFO;
      case "warn":
        return SeverityNumber.WARN;
      case "error":
        return SeverityNumber.ERROR;
      case "fatal":
        return SeverityNumber.FATAL;
      default:
        return SeverityNumber.INFO;
    }
  }

  private log(level: LogLevel, message: string, context?: LogContext) {
    if (!this.isInitialized || !this.logger) {
      // Silently skip if PostHog is not initialized
      return;
    }

    try {
      this.logger.emit({
        severityNumber: this.getSeverityNumber(level),
        severityText: level.toUpperCase(),
        body: message,
        attributes: {
          ...context,
          // Add app metadata
          app_name: "boomer",
          app_version: "1.0.0",
          organization_slug: env.VITE_ORGANIZATION_SLUG || "unknown",
          target: env.VITE_TARGET || "unknown",
          timestamp: new Date().toISOString(),
        },
      });
    } catch (error) {
      // Silently fail - PostHog logging is non-critical
      if (env.DEV) {
        console.error("Failed to send log to PostHog:", error);
      }
    }
  }

  debug(message: string, context?: LogContext) {
    this.log("debug", message, context);
  }

  info(message: string, context?: LogContext) {
    this.log("info", message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log("warn", message, context);
  }

  error(message: string, context?: LogContext) {
    this.log("error", message, context);
  }

  fatal(message: string, context?: LogContext) {
    this.log("fatal", message, context);
  }

  // Convenience method for logging errors with stack traces
  logError(error: Error, context?: LogContext) {
    this.error(error.message, {
      ...context,
      error_name: error.name,
      error_stack: error.stack,
    });
  }
}

// Export singleton instance
export const logger = new PostHogLogger();

// Export for testing/mocking
export { PostHogLogger };
